import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { post, get, patch } from '../../../../helpers/httpClient';
import { toast } from 'react-toastify';
import PageBasicFields from '../../../../helpers/PageBasicFields';
import ImageUploader from '../../../../helpers/ImageUploader';

function PosterEditor() {
    const navigate = useNavigate();
    const { id, pageId } = useParams();
    const [searchParams] = useSearchParams();
    const isEdit = !!pageId;

    const [form, setForm] = useState({
        slug: '',
        title: '',
        extra: {
            background: '',
            backgroundVertical: '',
            backgroundType: 'image',
            heading: '',
            subheading: '',
            overlayColor: '#ffffff',
            textShadeColor: '#000000',
        },
    });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(!!pageId);

    const titleText = useMemo(() => (isEdit ? 'Edit Poster' : 'New Poster'), [isEdit]);

    useEffect(() => {
        let isMounted = true;
        async function loadPage() {
            if (!isEdit) return;
            try {
                setLoading(true);
                const resp = await get(`pages/${pageId}`);
                const page = resp?.page || resp;
                if (!page) throw new Error('Not found');
                if (!isMounted) return;
                setForm({
                    slug: page.slug || '',
                    title: page.title || '',
                    description: page.description || '',
                    caption: page.caption || '',
                    preview: page.preview,
                    template: page.template || null,
                    components: page.components || { items: [] },
                    extra: {
                        background: page.extra?.background || '',
                        backgroundVertical: page.extra?.backgroundVertical || '',
                        backgroundType: page.extra?.backgroundType || 'image',
                        heading: page.extra?.heading || '',
                        subheading: page.extra?.subheading || '',
                        overlayColor: page.extra?.overlayColor || '#ffffff',
                        textShadeColor: page.extra?.textShadeColor || '#000000',
                    },
                });
            } catch (e) {
                if (isMounted) toast.error('Failed to load page data.');
            } finally {
                if (isMounted) setLoading(false);
            }
        }
        loadPage();
        return () => { isMounted = false; };
    }, [id, pageId, isEdit]);

    const setExtra = (patch) => setForm(prev => ({ ...prev, extra: { ...prev.extra, ...patch } }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!(form.title || '').trim()) {
            toast.error('Title is required.');
            return;
        }

        const payload = {
            type: 'poster',
            slug: (form.slug || '').trim(),
            title: (form.title || '').trim(),
            description: (form.description || '').trim() || null,
            caption: (form.caption || '').trim() || null,
            preview: form.preview || null,
            template: form.template || null,
            components: form.components || [],
            extra: {
                background: form.extra.background || '',
                backgroundVertical: form.extra.backgroundVertical || '',
                backgroundType: form.extra.backgroundType || 'image',
                heading: (form.extra.heading || '').trim() || null,
                subheading: (form.extra.subheading || '').trim() || null,
                overlayColor: form.extra.overlayColor || '#ffffff',
                textShadeColor: form.extra.textShadeColor || '#000000',
            },
        };

        try {
            setSaving(true);
            if (isEdit) {
                await patch(`websites/${id}/pages/${pageId}`, payload);
            } else {
                const parent = searchParams.get('parent');
                await post(`websites/${id}/pages`, { ...payload, order: 0, parent: parent || null });
            }
            navigate(`/websites/${id}?tab=structure`);
        } catch (err) {
            toast.error(isEdit ? 'Failed to update poster.' : 'Failed to create poster.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => navigate(-1);

    return (
        <Container className="mt-4">
            <Row>
                <Col>
                            <div className="structure-node-header mb-3">
                                <h2 className="m-0">{titleText}</h2>
                                <span className="page-type-badge page-type-badge--poster">Poster</span>
                            </div>

                            {loading ? (
                                <div className="text-center my-3"><Spinner animation="border" /></div>
                            ) : (
                                <Form onSubmit={handleSubmit}>
                                    <PageBasicFields
                                        form={form}
                                        setForm={setForm}
                                        websiteId={id}
                                        slugPlaceholder="e.g. home"
                                        titlePlaceholder="Enter page title"
                                        preserveEmptySlug={isEdit}
                                    />

                                    <div className="props-panel">
                                    <h6 className="theme-group-label mb-3">Poster</h6>

                                    <div className="form-row-horizontal mb-2">
                                        <Form.Label>Background type</Form.Label>
                                        <div className="bg-type-toggle">
                                            <button
                                                type="button"
                                                className={`bg-type-btn${form.extra.backgroundType === 'image' ? ' bg-type-btn--active' : ''}`}
                                                onClick={() => setExtra({ backgroundType: 'image', background: '', backgroundVertical: '' })}
                                            >
                                                🖼 Image
                                            </button>
                                            <button
                                                type="button"
                                                className={`bg-type-btn${form.extra.backgroundType === 'youtube-video' ? ' bg-type-btn--active' : ''}`}
                                                onClick={() => setExtra({ backgroundType: 'youtube-video', background: '', backgroundVertical: '' })}
                                            >
                                                ▶ Video
                                            </button>
                                        </div>
                                    </div>

                                    <div className="form-row-horizontal mb-2">
                                        <Form.Label>Background horizontal {form.extra.backgroundType === 'youtube-video' ? 'URL' : ''}</Form.Label>
                                        {form.extra.backgroundType === 'image' ? (
                                            <>
                                                <ImageUploader
                                                    websiteId={id}
                                                    onUploaded={(resp) => setExtra({ background: resp.url })}
                                                />
                                                {form.extra.background && (
                                                    <img
                                                        src={form.extra.background}
                                                        alt="Background horizontal"
                                                        className="poster-bg-preview mt-2"
                                                    />
                                                )}
                                            </>
                                        ) : (
                                            <Form.Control
                                                type="url"
                                                value={form.extra.background}
                                                onChange={(e) => setExtra({ background: e.target.value })}
                                                placeholder="https://youtu.be/..."
                                            />
                                        )}
                                    </div>

                                    <div className="form-row-horizontal mb-2">
                                        <Form.Label>Background vertical {form.extra.backgroundType === 'youtube-video' ? 'URL' : ''}</Form.Label>
                                        {form.extra.backgroundType === 'image' ? (
                                            <>
                                                <ImageUploader
                                                    websiteId={id}
                                                    onUploaded={(resp) => setExtra({ backgroundVertical: resp.url })}
                                                />
                                                {form.extra.backgroundVertical && (
                                                    <img
                                                        src={form.extra.backgroundVertical}
                                                        alt="Background vertical"
                                                        className="poster-bg-preview mt-2"
                                                    />
                                                )}
                                            </>
                                        ) : (
                                            <Form.Control
                                                type="url"
                                                value={form.extra.backgroundVertical}
                                                onChange={(e) => setExtra({ backgroundVertical: e.target.value })}
                                                placeholder="https://youtu.be/..."
                                            />
                                        )}
                                    </div>

                                    <div className="form-row-horizontal mb-2">
                                        <Form.Label>Heading</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={form.extra.heading}
                                            onChange={(e) => setExtra({ heading: e.target.value })}
                                            placeholder="Main heading (optional)"
                                        />
                                    </div>

                                    <div className="form-row-horizontal mb-2">
                                        <Form.Label>Subheading</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={form.extra.subheading}
                                            onChange={(e) => setExtra({ subheading: e.target.value })}
                                            placeholder="Optional subheading"
                                        />
                                    </div>

                                    <div className="form-row-horizontal mb-2">
                                        <Form.Label>Overlay text colour</Form.Label>
                                        <Form.Control
                                            type="color"
                                            value={form.extra.overlayColor}
                                            onChange={(e) => setExtra({ overlayColor: e.target.value })}
                                            className="color-input-sm"
                                        />
                                    </div>

                                    <div className="form-row-horizontal mb-2">
                                        <Form.Label>Text shade colour</Form.Label>
                                        <Form.Control
                                            type="color"
                                            value={form.extra.textShadeColor}
                                            onChange={(e) => setExtra({ textShadeColor: e.target.value })}
                                            className="color-input-sm"
                                        />
                                    </div>

                                    <div className="form-row-horizontal mb-3">
                                        <Form.Label>Preview</Form.Label>
                                        <div
                                            className="poster-mini-preview"
                                            style={form.extra.background && form.extra.backgroundType === 'image' ? { backgroundImage: `url(${form.extra.background})` } : {}}
                                        >
                                            <div className="poster-mini-overlay" style={{ background: `${form.extra.textShadeColor}66` }}>
                                                {form.extra.heading || form.extra.subheading ? (
                                                    <>
                                                        {form.extra.heading && <div className="poster-mini-heading" style={{ color: form.extra.overlayColor }}>{form.extra.heading}</div>}
                                                        {form.extra.subheading && <div className="poster-mini-subheading" style={{ color: form.extra.overlayColor }}>{form.extra.subheading}</div>}
                                                    </>
                                                ) : (
                                                    <div className="poster-mini-placeholder" style={{ color: form.extra.overlayColor }}>Enter a heading to preview</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    </div>

                                    <div className="form-actions">
                                        <Button type="submit" className="primary-button" disabled={saving}>
                                            {saving ? (<><Spinner animation="border" size="sm" className="me-2" />Saving...</>) : 'Save'}
                                        </Button>
                                        <Button className="primary-button" onClick={handleCancel} disabled={saving}>Cancel</Button>
                                    </div>
                                </Form>
                            )}
                </Col>
            </Row>
        </Container>
    );
}

export default PosterEditor;
